import React from "react";
import { Button } from "@components/ui/button";
import { requestApplySuperoneApiDataEmbeddings } from "@helpers/requestApplySuperoneApiDataEmbeddings";
import { Card } from "@components/ui/card";
import { TextCursorInput } from "lucide-react";

type PromptPlayerDataProps = {
  prompt: string;
  setPrompt: (val: string) => void;
  answer: string | null;
  loading: boolean;
  error: string | null;
  onSubmit: (e: React.FormEvent) => void;
};

const PromptPlayersData = ({
  prompt,
  setPrompt,
  answer,
  loading,
  error,
  onSubmit,
}: PromptPlayerDataProps) => {
  const [apiData, setApiData] = React.useState<any>(null);
  const [apiLoading, setApiLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState("");

  // Embedding button state
  const [embeddingLoading, setEmbeddingLoading] = React.useState(false);
  const [embeddingResult, setEmbeddingResult] = React.useState<string | null>(
    null
  );
  const [embeddingError, setEmbeddingError] = React.useState<string | null>(
    null
  );

  const handleApplyEmbeddings = async () => {
    setEmbeddingLoading(true);
    setEmbeddingResult(null);
    setEmbeddingError(null);
    try {
      const res = await requestApplySuperoneApiDataEmbeddings();
      setEmbeddingResult(
        typeof res === "string"
          ? res
          : res?.message || "Embeddings applied successfully."
      );
    } catch (e: any) {
      setEmbeddingError(e.message || "Failed to apply embeddings");
    } finally {
      setEmbeddingLoading(false);
    }
  };

  const handleApiData = async () => {
    setApiLoading(true);
    setApiError("");
    setApiData(null);
    try {
      // Use environment variable for API URL
      const apiBaseUrl = process.env.PARCEL_API_URL || "";
      const apiUrl = `${apiBaseUrl}/api/prompt-player-data`;
      const res = await fetch(apiUrl);
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      const data = await res.json();
      setApiData(data);
    } catch (err) {
      setApiError(err instanceof Error ? err.message : String(err));
    } finally {
      setApiLoading(false);
    }
  };

  const suggestedQuestions = [
    "How many new fans did we have last week?",
    "What are the top 5 games in the last 30 days?",
    "On what days do we get the most active fans?",
    "What countries are most fans from?",
    "What are the top countries by hours played?",
    "Which clubs played the least games in the last 3 months?",
    "What is the most common referral method?",
    "Which devices are used the most?",
  ];

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200 overflow-hidden p-6 border border-gray-800">
      <h2 className="text-lg font-semibold font-heading mb-4 flex items-center text-superone-charcoal">
        <TextCursorInput className="mr-2 h-5 w-5 text-pink-500" />
        Prompt Players Data
      </h2>
      <span className="text-yellow-700 bg-yellow-100 rounded px-2 py-1 font-medium mb-4 inline-block">
        In preview mode, AI information and embeddings include all data from
        2025-05-01 to present day.
      </span>
      {/* Suggested Questions */}
      <div className="mb-4">
        <div className="font-medium text-gray-700 mb-2">
          Suggested questions:
        </div>
        <div className="flex flex-wrap gap-2">
          {suggestedQuestions.map((q) => (
            <button
              key={q}
              type="button"
              className="text-blue-700 bg-blue-100 rounded px-2 py-1 font-normal inline-block cursor-pointer"
              onClick={() => setPrompt(q)}
              disabled={loading}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="font-medium text-gray-700 w-full">
          Your question:
          <textarea
            className="mt-2 w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px] resize-vertical text-base font-normal"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="How many new fans are there this month?"
            required
            disabled={loading}
            rows={4}
          />
        </label>
        <div className="flex flex-row items-center justify-between gap-2 w-full">
          <button
            type="submit"
            className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50 cursor-pointer"
            disabled={loading || !prompt.trim()}
          >
            {loading ? "Asking..." : "Ask"}
          </button>
        </div>
        {error ? (
          <span className="text-red-700 bg-red-100 rounded px-2 py-1 font-normal mb-4 inline-block">
            {error}
          </span>
        ) : null}
        {answer ? (
          <div className="mt-2">
            {(() => {
              try {
                const parsed =
                  typeof answer === "string" ? JSON.parse(answer) : answer;
                let content =
                  parsed &&
                  typeof parsed === "object" &&
                  typeof parsed.content === "string"
                    ? parsed.content
                    : typeof answer === "string"
                    ? answer
                    : String(answer);
                // Format: replace '\n' with <br /> and '- ' at line start with <li>
                // Split into lines, wrap bullet lines in <li>, others in <p> or <span>
                const lines = content
                  .split(/\r?\n/)
                  .filter((l: string) => l.trim() !== "");
                const formatted = lines.map((line: string, idx: number) => {
                  if (line.trim().startsWith("- ")) {
                    return (
                      <li key={idx} className="ml-6 list-disc">
                        {line.trim().slice(2)}
                      </li>
                    );
                  }
                  // Next step line
                  if (line.trim().toLowerCase().startsWith("next step:")) {
                    return (
                      <div key={idx} className="text-blue-700 font-medium">
                        {line.trim()}
                      </div>
                    );
                  }
                  // Main answer sentence
                  return (
                    <div key={idx} className="mb-1">
                      {line.trim()}
                    </div>
                  );
                });
                // Group <li> into a <ul>
                const bullets = formatted.filter((el: any) => el.type === "li");
                const nonBullets = formatted.filter(
                  (el: any) => el.type !== "li"
                );
                return (
                  <>
                    {nonBullets
                      .filter(
                        (el: any) =>
                          el.type !== "div" ||
                          !el.props.className?.includes("mt-3")
                      )
                      .map((el: any) => el)}
                    {bullets.length > 0 ? (
                      <ul className="my-2 ml-4">{bullets}</ul>
                    ) : null}
                    {nonBullets
                      .filter((el: any) => el.props.className?.includes("mt-3"))
                      .map((el: any) => el)}
                  </>
                );
              } catch {
                return typeof answer === "string" ? answer : String(answer);
              }
            })()}
          </div>
        ) : null}
      </form>
    </Card>
  );
};

export default PromptPlayersData;
